import type { SprintEffect } from '../../../data/full/sportif';
import { hurtEnemy } from '../../effects';
import { applyEnemyStatus } from '../../enemy-status';
import { applyPlayerBoost } from '../../player-boosts';
import type { SkillModule } from '../../skills/types';
import type { EnemyState, PlayerState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';

const SPRINT_BOOST_ID = 'sprint';

export const sprint: SkillModule<SprintEffect> = {
  cast(ctx, player, effect) {
    applyPlayerBoost(ctx, player, {
      id: SPRINT_BOOST_ID,
      modifiers: [{ stat: 'speedMul', mul: effect.speedMul }],
      ticks: sprintTicks(ctx, effect),
    });
  },

  update(ctx) {
    for (const player of ctx.state.players) {
      const boost = player.boosts?.find((running) => running.id === SPRINT_BOOST_ID);
      if (boost === undefined || player.downed) {
        continue;
      }
      const effect = effectOf(ctx, player);
      const starting = boost.ticksLeft === sprintTicks(ctx, effect);
      for (const enemy of ctx.state.enemies) {
        if (enemy.hp > 0 && touches(player, enemy) && (starting || !touched(player, enemy))) {
          knock(ctx, player, effect, enemy);
        }
      }
    }
  },
};

export function isSprinting(player: Pick<PlayerState, 'boosts'>): boolean {
  return player.boosts?.some((boost) => boost.id === SPRINT_BOOST_ID) === true;
}

function sprintTicks(ctx: StepContext, effect: SprintEffect): number {
  return effect.beats * ctx.tempo.ticksPerBeat;
}

function effectOf(ctx: StepContext, player: PlayerState): SprintEffect {
  const effect = ctx.content.classes.get(player.classId)?.skill.effect;
  if (effect?.kind !== 'sprint') {
    throw new Error(`player ${String(player.id)} of class "${player.classId}" has no sprint`);
  }
  return effect;
}

function touches(player: PlayerState, enemy: EnemyState): boolean {
  const reach = player.radius + enemy.radius;
  const dx = enemy.x - player.x;
  const dy = enemy.y - player.y;
  return dx * dx + dy * dy <= reach * reach;
}

// Already touching at the start of the step: running into a bad vibe knocks it once.
function touched(player: PlayerState, enemy: EnemyState): boolean {
  const reach = player.radius + enemy.radius;
  const dx = enemy.prevX - player.prevX;
  const dy = enemy.prevY - player.prevY;
  return dx * dx + dy * dy <= reach * reach;
}

function knock(
  ctx: StepContext,
  player: PlayerState,
  effect: SprintEffect,
  enemy: EnemyState,
): void {
  const { state } = ctx;
  const power = statValue(player, 'skillPowerMul', 1);
  hurtEnemy(state, enemy, effect.damage * power, player.id, {
    x: player.prevX,
    y: player.prevY,
  });
  applyEnemyStatus(state, enemy, 'frozen', effect.knockBeats * ctx.tempo.ticksPerBeat);
}
